import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactElement,
} from 'react';

import { LangProvider } from '@pushwoosh/dumb-components/polyglot';
import type { LangMessages } from '@pushwoosh/dumb-components/polyglot';

import { browserTimezone } from '~/src/helpers/common.helpers';
import { messages as enMessages } from './messages_en';
import { loadMessages } from './loadMessages';
import {
  applyDocumentLanguage,
  persistLanguage,
  resolveInitialLanguage,
} from './language';
import type { PolyglotMessages } from './type';

type LanguageContextValue = {
  language: string;
  setLanguage: (code: string) => void;
};

// Resolved once at module load — the initial language for this page view.
const initialLanguage = resolveInitialLanguage();

const LanguageContext = createContext<LanguageContextValue>({
  language: initialLanguage,
  setLanguage: () => {},
});

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}

export function LanguageProvider({ children }: PropsWithChildren): ReactElement {
  const [language, setLanguageState] = useState<string>(initialLanguage);
  // Start from the English baseline so children render instantly; the localized
  // dictionary swaps in once its chunk loads (no blank screen, brief en flash
  // only for non-en initial languages).
  const [messages, setMessages] = useState<LangMessages<PolyglotMessages>>(enMessages);

  useLayoutEffect(() => {
    applyDocumentLanguage(language);
  }, [language]);

  useEffect(() => {
    let cancelled = false;

    loadMessages(language).then((loaded) => {
      if (!cancelled) {
        setMessages(loaded);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [language]);

  const setLanguage = useCallback((code: string) => {
    persistLanguage(code);
    setLanguageState(code);
  }, []);

  const contextValue = useMemo(
    () => ({ language, setLanguage }),
    [language, setLanguage],
  );

  // Memoize the dictionary map so LangProvider's internal useMemo can
  // short-circuit — otherwise a fresh object every render re-parses all
  // strings through stringToAst on any re-render.
  const languagesMap = useMemo(
    () => ({ [language]: messages }),
    [language, messages],
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      <LangProvider language={language} languages={languagesMap} timezone={browserTimezone()}>
        {children}
      </LangProvider>
    </LanguageContext.Provider>
  );
}
